import {
  QUESTION_SEPARATOR,
  PREDEFINED_PROVIDED_ELSEWHERE,
  PREDEFINED_NOT_APPLICABLE,
} from '@/_constants/questions';
import {
  Question,
  Questionnaire,
  QuestionType,
} from '@/_types/models/questionnaire';
import { Section } from '@/_types/models/sections';
import { isEmpty } from '@/_utils/strings';
import type { QuestionWithLevels } from '@/_types/questions';
import { QuestionnaireTemplate } from '@/_types/models/questionnaireTemplate';

export const getIsPredefinedAnswer = (question?: Question): boolean => {
  if (
    question?.answer === PREDEFINED_PROVIDED_ELSEWHERE ||
    question?.answer_by ||
    question?.answer === PREDEFINED_NOT_APPLICABLE
  ) {
    return true;
  }

  return false;
};

export const readAnswerArray = (answer?: string): Array<string> => {
  if (!answer) return [];
  if (!answer.includes(QUESTION_SEPARATOR)) return [answer];

  const answers: Array<string> = answer.split(QUESTION_SEPARATOR);

  return answers;
};

export const writeAnswerArray = (answer: Array<string>): string => {
  if (!answer || answer.length === 0) return '';

  const uniqueAnswers = Array.from(new Set(answer));
  const answers: string = uniqueAnswers.join(QUESTION_SEPARATOR);

  return answers;
};

export const toggleAnswerOption = (
  answers: Array<string>,
  option: string,
): string => {
  if (!answers) return '';

  const isChecked: boolean = answers.includes(option);
  const filteredAnswers = answers.filter((answer) => answer !== option);

  if (isChecked) return writeAnswerArray(filteredAnswers);

  return writeAnswerArray([...answers, option]);
};

export const isQuestionAnswered = (question: Question): boolean => {
  if (!question?.id) return false;

  const hasValidAnswer: boolean = Boolean(
    !isEmpty(question.answer) && question.answer !== '0',
  );
  const isAnswerable: boolean = hasValidAnswer || Boolean(question.optional);

  return question.question_type === 'entries'
    ? isAnswerable && !question.answer_by
    : isAnswerable;
};

export const getParentQuestion = (
  section: Section | Partial<Section>,
  question: Question,
): Question | undefined => {
  return section?.questionnaire_questions?.find(
    (q: Question) => q.id === question.follow_up_question_id,
  );
};

export const isQuestionTriggered = (
  section: Section | Partial<Section>,
  question?: Question,
): boolean => {
  if (!question?.follow_up_question_id) return true;

  const parent = getParentQuestion(section, question);

  const isMultiple: boolean =
    parent?.question_type === 'multiple' || parent?.question_type === 'select';

  const questionTriggered =
    parent &&
    question.trigger_for_follow_up_question &&
    parent.answer &&
    ((isMultiple &&
      parent.answer
        .split('&!&')
        .includes(question.trigger_for_follow_up_question)) ||
      parent.answer === question.trigger_for_follow_up_question);

  const triggered: boolean = isQuestionTriggered(section, parent);

  return Boolean(triggered && questionTriggered);
};

export const getTriggeredQuestions = (
  question: QuestionWithLevels,
  includeEntries?: boolean,
): {
  hasTriggeredQuestions: boolean;
  questions: Array<QuestionWithLevels>;
} => {
  const isValidType: boolean = Boolean(
    includeEntries || question.question_type !== 'entries',
  );

  const questions: Array<QuestionWithLevels> =
    question.questions?.filter((q) => {
      // Always filter out Template triggers
      if (q.trigger_for_follow_up_question === 'Template') return false;

      // If includeEntries is true, include all non-Template questions
      if (includeEntries) return true;

      // Must have trigger_for_follow_up_question for non-entry questions
      if (!q.trigger_for_follow_up_question) return false;

      // Trigger validation logic for non-entry questions
      const answerArray: Array<string> = !!question.answer?.includes(
        QUESTION_SEPARATOR,
      )
        ? question.answer.split(QUESTION_SEPARATOR)
        : [question.answer];

      return answerArray.includes(q.trigger_for_follow_up_question);
    }) || [];

  const hasTriggeredQuestions: boolean = Boolean(
    questions?.length && question.answer && isValidType,
  );

  return { hasTriggeredQuestions, questions };
};

export const mapQuestionsToLevels = (
  questions: Array<Question>,
): Array<QuestionWithLevels> => {
  const questionMap = new Map<number, QuestionWithLevels>();

  questions.forEach((question) => {
    questionMap.set(question.id, {
      ...question,
      questions: [],
      status:
        !!question?.answer && question?.answer !== '0' && !question?.answer_by
          ? 'completed'
          : !!question?.answer_by
            ? 'pending'
            : 'incomplete',
    });
  });

  const roots: Array<QuestionWithLevels> = [];

  questionMap.forEach((node, _id) => {
    if (node.follow_up_question_id) {
      const parent = questionMap.get(node.follow_up_question_id);
      parent?.questions.push(node);
    } else {
      roots.push(node);
    }
  });

  // I (Jordan) once ran a performance check on this sorting to to ensure it wasn't horrible and it only took 0.09999996423721313 milliseconds. So there are no performance concerns here.
  // This is the code I ran:
  // ```
  // const time0 = performance.now();
  // ...code
  // const time1 = performance.now();
  // console.log('mapQuestionsToLevels took', time1 - time0, 'milliseconds');
  // ```
  roots.sort((a, b) => {
    if (typeof a.order === 'number' && typeof b.order === 'number') {
      return a.order - b.order;
    }

    return a.id - b.id;
  });

  roots.forEach((root) => {
    root.questions.sort((a, b) => {
      if (typeof a.order === 'number' && typeof b.order === 'number') {
        return a.order - b.order;
      }

      return a.id - b.id;
    });

    if (root.questions.length > 0) {
      root.questions.forEach((child) => {
        child.questions.sort((a, b) => {
          if (typeof a.order === 'number' && typeof b.order === 'number') {
            return a.order - b.order;
          }

          return a.id - b.id;
        });
      });
    }
  });

  return roots;
};

export const getPendingQuestions = (
  questions: Array<Question> = [],
): Array<Question> => {
  if (!questions?.length) return [];

  const pendingQuestions: Array<Question> = questions.filter(
    (q) => !!q.answer_by,
  );

  return pendingQuestions;
};

export const getIncompleteQuestions = (
  questions: Array<Question> = [],
): Array<Question> => {
  if (!questions?.length) return [];

  const triggeredQuestions = getTriggeredQuestionsFromQuestions(questions);
  const incompleteQuestions: Array<Question> = triggeredQuestions.filter(
    (q) => !q.optional && (!q.answer || !!q.answer_by),
  );

  return incompleteQuestions;
};

export const getSectionQuestions = (
  allQuestions: Array<Question> = [],
  section: Section | Partial<Section>,
) => {
  if (!section?.id) return [];

  const sectionQuestions: Array<Question> =
    allQuestions
      ?.filter((q) => q.section_id === section?.id)
      .filter((q) => isQuestionTriggered(section, q)) || [];

  return filterClientEntryQuestions(sectionQuestions);
};

export const getTriggeredQuestionsFromQuestions = (
  questions: Array<Question>,
): Array<Question> => {
  if (!questions?.length) return [];

  const triggeredQuestions = questions?.filter((q) => {
    if (!q.follow_up_question_id) return true;

    const parent: Question | undefined = questions.find(
      (pq) => pq.id === q.follow_up_question_id,
    );

    if (!parent?.answer) return false;
    if (!q.trigger_for_follow_up_question) return false;

    const answerArray: Array<string> = parent.answer.includes(
      QUESTION_SEPARATOR,
    )
      ? parent.answer.split(QUESTION_SEPARATOR)
      : [parent.answer];

    return answerArray.includes(q.trigger_for_follow_up_question);
  });

  const triggeredPotentialParentIds: Array<number> = triggeredQuestions.map(
    (tq) => tq.id,
  );

  // For some reason, a grandchild question of a non-applicable grandparent question was still coming through (but the intermediate parent was not). This filters out any questions that have a follow_up_question_id that is not being initially returned as a triggered question (in `triggeredQuestions`) because we wouldn't want to return any child question whose parent is not considered triggered.
  return triggeredQuestions.filter((question) => {
    if (!question.follow_up_question_id) return true;
    if (triggeredPotentialParentIds.includes(question.follow_up_question_id)) {
      return true;
    }

    return false;
  });
};

export const getHasRolloverItem = (
  allQuestions: Array<Question>,
  questionId: number,
  option: string,
): boolean => {
  return allQuestions.some(
    (q) =>
      q.follow_up_question_id === questionId &&
      q.trigger_for_follow_up_question === option &&
      q.uploaded_last_year,
  );
};

export const filterClientEntryQuestions = (
  allQuestions: Array<Question> = [],
): Array<Question> => {
  if (!allQuestions?.length) return [];

  return allQuestions.filter((question) => {
    if (!question.follow_up_question_id) return true;

    const parent = allQuestions.find(
      (q) => q.id === question.follow_up_question_id,
    );
    return parent?.question_type !== 'entries';
  });
};

export const removeGivenQuestionType = (
  questions: Array<Question>,
  type: QuestionType,
) => {
  if (!questions) return [];
  if (!type) return questions;

  return questions.filter((question) => question.question_type !== type);
};

export const getQuestionsForSection = (
  questions: Array<Question> = [],
  sectionType?: number,
  questionnaire?: Questionnaire | QuestionnaireTemplate,
  areUploadsHidden?: boolean,
): Array<Question> => {
  // Return only upload type question for document checklist sections, regardless of feature flags
  if (sectionType === 10) {
    const uploadQuestions: Array<Question> = questions?.filter(
      (q) => q.question_type === 'upload',
    );

    const hasValidCustomDocumentsNeeded: boolean = Array.isArray(
      questionnaire?.documents_uploaded_and_needed,
    );

    return hasValidCustomDocumentsNeeded
      ? questionnaire?.documents_uploaded_and_needed || []
      : uploadQuestions;
  }

  // Hide upload-type questions within regular sections when preference is enabled
  if (areUploadsHidden) {
    return questions?.filter((q) => q.question_type !== 'upload');
  }

  return questions;
};
